<template>
  <div class="phone-auth-form-container">
    <!-- 🔹 Заголовок -->
    <h2 class="register-title">
      {{ step === 1 ? "Регистрация нового пользователя" : "Введите код из SMS" }}
    </h2>

    <!-- 🔹 Описание -->
    <p class="register-description">
      {{ step === 1
        ? "Присоединяйтесь к платформе Hr3!\nВведите номер телефона для создания аккаунта."
        : "Мы отправили код подтверждения на ваш номер.\nПожалуйста, введите код ниже."
      }}
    </p>

    <!-- 🔹 Поля -->
    <div class="input-container">
      <!-- Шаг 1: Ввод телефона -->
      <template v-if="step === 1">
        <TextField
          v-model="phoneNumber"
          placeholder="+39 345 678 9012"
          :error="phoneError"
          type="tel"
        />
        <p v-if="phoneError" class="error-message">Введите корректный номер телефона</p>
      </template>

      <!-- Шаг 2: Ввод кода -->
      <template v-else>
        <TextField
          v-model="smsCode"
          placeholder="Код из SMS"
          :error="codeError"
          type="text"
        />
        <p v-if="codeError" class="error-message">Введите код из сообщения</p>
      </template>

      <!-- Кнопка -->
      <button
        class="register-button"
        :disabled="isLoading || (step === 1 ? !isPhoneValid : !isCodeValid)"
        @click="handleSubmit"
      >
        {{ step === 1 ? "Получить код" : "Подтвердить код" }}
      </button>

      <p v-if="isLoading" class="support-text">⏳ Пожалуйста, подождите...</p>
    </div>

    <!-- 🔹 reCAPTCHA контейнер -->
    <div id="recaptcha-container" style="position: absolute; left: -9999px;"></div>

    <!-- 🔔 Модалка об ограничениях SMS -->
    <div v-if="showSmsRestriction" class="modal-overlay" @click.self="showSmsRestriction = false">
      <div class="modal">
        <h3 class="modal-title">SMS подтверждение недоступно</h3>
        <p class="modal-text">
          Отправка SMS на российские номера может быть заблокирована Google. 
          Пожалуйста, воспользуйтесь другим способом авторизации.
        </p>
        <button class="modal-button" @click="showSmsRestriction = false">Понятно</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import PhoneAuthService from "../../api/PhoneAuthService.js";
import TextField from "../../components/TextField.vue";
import UserAuthService from "../../api/UserAuthService.js";
import UserService from "../../api/UserService.js";

const router = useRouter();

const step = ref(1);
const phoneNumber = ref("");
const smsCode = ref("");
const isLoading = ref(false);
const confirmationResult = ref(null);
const showSmsRestriction = ref(false);
const phoneError = ref(false);
const codeError = ref(false);

/**
 * 🔹 Форматирование номера
 */
const normalizePhone = (raw) => {
  const digits = raw.replace(/\D+/g, "");
  if (digits.length === 11 && digits.startsWith("8")) return "+7" + digits.slice(1);
  if (digits.length === 11 && digits.startsWith("7")) return "+7" + digits.slice(1);
  if (raw.trim().startsWith("00")) return "+" + raw.trim().slice(2).replace(/\D+/g, "");
  if (raw.trim().startsWith("+")) return "+" + digits;
  return "+" + digits;
};

const isPhoneValid = computed(() => /^\+\d{10,15}$/.test(normalizePhone(phoneNumber.value)));
const isCodeValid = computed(() => /^\d{4,8}$/.test(smsCode.value.trim()));

async function handleSubmit() {
  if (step.value === 1) {
    await handleSendCode();
  } else {
    await handleVerifyCode();
  }
}

/**
 * 🔹 Отправка SMS
 */
async function handleSendCode() {
  if (!isPhoneValid.value) {
    phoneError.value = true;
    return;
  }

  phoneError.value = false;
  isLoading.value = true;

  try {
    // ⚡️ Инициализируем reCAPTCHA только один раз
    if (!PhoneAuthService.recaptchaVerifier) {
      await PhoneAuthService.initRecaptcha();
    }

    const phoneE164 = normalizePhone(phoneNumber.value.trim());
    console.log("📲 Отправляем SMS на:", phoneE164);
    confirmationResult.value = await PhoneAuthService.sendCode(phoneE164);

    console.log("✅ Код успешно отправлен");
    step.value = 2;
  } catch (error) {
    console.error("❌ Ошибка отправки кода:", error);

    if (error.code === "auth/too-many-requests") {
      alert("Слишком много попыток. Попробуйте позже.");
    } else if (error.code === "auth/invalid-phone-number") {
      phoneError.value = true;
    } else if (error.code === "auth/captcha-check-failed") {
      alert("Ошибка проверки reCAPTCHA. Обновите страницу и попробуйте снова.");
    } else if (phoneNumber.value.startsWith("+7") || phoneNumber.value.startsWith("8")) {
      showSmsRestriction.value = true;
    } else {
      alert("Ошибка при отправке SMS. Проверьте номер или попробуйте позже.");
    }
  } finally {
    isLoading.value = false;
  }
}

/**
 * 🔹 Проверка кода и редирект
 */
async function handleVerifyCode() {
  if (!isCodeValid.value) {
    codeError.value = true;
    return;
  }

  codeError.value = false;
  isLoading.value = true;

  try {
    const user = await PhoneAuthService.verifyCode(confirmationResult.value, smsCode.value.trim());
    console.log("✅ Авторизация успешна:", user);

    const idToken = await user.getIdToken(true);
    localStorage.setItem("auth_token", idToken);
    localStorage.setItem("user_id", user.uid);

    // 🧠 Проверяем статус пользователя
    let userData = null;
    try {
      userData = await UserAuthService.checkUserStatus(user.uid, 1);
      console.log("📊 User status:", userData);
    } catch (err) {
      console.warn("⚠️ checkUserStatus упал:", err);
      await router.push("/registration");
      return;
    }

    // 🔀 Направляем по статусу
    if (userData?.isRegistrationComplete) {
      try {
        await UserService.getProfile(user.uid);
      } catch (e) {
        console.warn("⚠️ Не удалось получить профиль:", e);
      }
      await router.push("/feed");
    } else {
      await router.push("/registration");
    }
  } catch (error) {
    console.error("❌ Ошибка верификации:", error);
    codeError.value = true;
  } finally {
    isLoading.value = false;
  }
}
</script>
<style scoped>
/* ✅ Основной контейнер */
.phone-auth-form-container {
  width: 385px;
  height: 550px;
  background-color: #2B2B2B;
  border-radius: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
}

/* 🌞 Светлая тема: переопределения */
[data-theme="light"] .phone-auth-form-container {
  background-color: #F7F7F7; /* светлый фон */
}

[data-theme="light"] .login-title,
[data-theme="light"] .login-description,
[data-theme="light"] .or-text,
[data-theme="light"] .register-container {
  color: #111; /* тёмный текст на светлом фоне */
}

[data-theme="light"] .email-login-button {
  color: white;
  background: linear-gradient(to right, #2173E5, #204F90);
}

[data-theme="light"] .email-login-button:disabled {
  background: #ccc;
  opacity: 0.6;
}

/* ✅ Заголовок */
.register-title {
    font-size: 16px;
    font-family: "Eastman-Bold", sans-serif;
    color: white;
    text-align: center;
    margin-bottom: 10px;
}

/* ✅ Описание */
.register-description {
    font-size: 14px;
    font-family: "Eastman-Regular", sans-serif;
    color: white;
    text-align: center;
    line-height: 1.4;
    max-width: 80%;
    margin-bottom: 20px;
}

/* ✅ Контейнер для полей */
.input-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    /* ✅ Центрируем элементы */
    gap: 10px;
    width: 100%;
}

/* ✅ Ошибка в поле */
.input-error {
    border: 1px solid red !important;
}

/* ✅ Сообщение об ошибке */
.error-message {
    font-size: 12px;
    color: red;
    text-align: center;
    /* ✅ Центрируем текст ошибки */
    width: 100%;
    margin: 0;
    font-family: "Eastman-Medium", sans-serif;
    /* ✅ Добавляем шрифт */
}

/* ✅ Кнопка "Зарегистрироваться" */
.register-button {
    width: 260px;
    height: 50px;
    border-radius: 25px;
    border: none;
    font-size: 14px;
    font-family: "Eastman-Bold", sans-serif;
    color: white;
    background: linear-gradient(to right, #2173E5, #204F90);
    cursor: pointer;
    transition: all 0.3s ease;
    margin-top: 20px;
}

/* 🔹 Эффект наведения */
.register-button:hover {
    transform: scale(1.05);
    opacity: 0.9;
}

/* 🔹 Отключённая кнопка */
.register-button:disabled {
    background: #4b4b4b;
    cursor: not-allowed;
    opacity: 0.6;
}

/* ✅ Текст поддержки */
.support-text {
    font-size: 12px;
    font-family: "Eastman-Regular", sans-serif;
    color: white;
    text-align: center;
    margin-top: 15px;
}

/* 🔹 Ссылка внутри поддержки */
.support-text a {
    color: #2887F7;
    text-decoration: none;
    font-family: "Eastman-Bold", sans-serif;
}

/* 🔹 Hover для ссылки */
.support-text a:hover {
    text-decoration: underline;
}

/* 🔔 Модалка */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  width: 420px;
  background: #2B2B2B;
  border-radius: 12px;
  padding: 20px;
}

[data-theme="light"] .modal {
  background: #FFFFFF;
}

.modal-title {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-family: "Eastman-Bold", sans-serif;
  color: white;
}

[data-theme="light"] .modal-title {
  color: #111;
}

.modal-text {
  margin: 0 0 16px 0;
  font-size: 14px;
  font-family: "Eastman-Regular", sans-serif;
  color: white;
  line-height: 1.45;
}

[data-theme="light"] .modal-text {
  color: #111;
}

.modal-button {
  width: 100%;
  height: 44px;
  border: none;
  border-radius: 10px;
  color: white;
  font-size: 14px;
  font-family: "Eastman-Bold", sans-serif;
  background: linear-gradient(to right, #2173E5, #204F90);
  cursor: pointer;
}

/* 🌞 Светлая тема */
[data-theme="light"] .register-form-container {
  background-color: #F7F7F7;
}

[data-theme="light"] .register-title,
[data-theme="light"] .register-description,
[data-theme="light"] .error-message,
[data-theme="light"] .support-text {
  color: #111;
}

[data-theme="light"] .register-button {
  color: white;
  background: linear-gradient(to right, #2173E5, #204F90);
}

[data-theme="light"] .register-button:disabled {
  background: #ccc;
  opacity: 0.6;
}

[data-theme="light"] .support-text a {
  color: #2173E5;
}

</style>